import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Dropdown,
  Input,
  Menu,
  Row,
  Select,
  Typography,
  DatePicker,
  Checkbox,
} from 'antd';
import Api from 'libs/api/index';
import axios from 'axios';
import AuthService from '../../services/Auth';
import { Button } from 'components';
import { PlusOutlined, CheckOutlined, FilterOutlined } from '@ant-design/icons';
import styles from './style.module.scss';
import FilterWrapper from './wrapper';
import locale from 'antd/es/date-picker/locale/it_IT';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useFilter } from 'hooks/useFilter';
import _, { debounce } from 'lodash';
import Helpers from 'helpers';
import { renderGroupedShopOptions } from 'components/shopOptions';
import moment from 'moment';
import { useRecoilValue } from 'recoil';
import { getPages } from '../../hooks/useUser';
import { FilterIcon, DeleteIcon } from 'icons';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { RangePicker } = DatePicker;

const INPUT_DEBOUNCE_MS = 1500;

// Text filter keeps what the user types (no remount on page re-renders) and
// follows the URL when it changes from outside (back/forward, restore).
const FilterInput = ({ urlValue = '', onChange, ...rest }) => {
  const [text, setText] = useState(urlValue);
  const lastSentRef = useRef(null);

  useEffect(() => {
    // URL caught up with what this input sent: keep what is typed now
    if (lastSentRef.current !== null && urlValue === lastSentRef.current) {
      lastSentRef.current = null;
      return;
    }
    setText(urlValue);
  }, [urlValue]);

  return (
    <Input
      {...rest}
      value={text}
      onChange={(e) => {
        const { value } = e.target;
        setText(value);
        lastSentRef.current = value;
        onChange(value);
      }}
    />
  );
};

const Filters = ({
  filters = [],
  filterByShoppingCenter,
  allFilters = false,
  defaultShowAllFilters = true,
  sortFilters = false,
}) => {
  let [searchParams, setSearchParams] = useSearchParams();
  const [menuSelectedKeys, setMenuSelectedKeys] = useState([]);
  const [showFilters, setShowFilters] = useState([]);
  const { handleChangeFilters: onChange, handleCloseFilter: onClose } =
    useFilter({ filterByShoppingCenter });

  const [insegnaValues, setInsegnaValues] = useState([]);
  // village used to narrow Insegna (sortFilters), applied to inactive too
  const [insegnaVillage, setInsegnaVillage] = useState('');
  const [villageValues, setVillageValues] = useState([]);

  const { pathname = '' } = useLocation();
  const pages = useRecoilValue(getPages);
  const { t } = useTranslation();
  const currentPage = useMemo(
    () =>
      pages.find((i) => {
        return (pathname + '/').includes(i.basePath + '/');
      }),
    [pathname, pages],
  );
  const currentSubMenuValue = useMemo(
    () => currentPage?.subLinks?.find((i) => pathname.includes(i.link)),
    [pathname, currentPage],
  );

  const showAllFilters = useMemo(() => {
    return defaultShowAllFilters && !pathname.includes('/analytics');
  }, [pathname, defaultShowAllFilters]);

  const avilableFilters = currentSubMenuValue?.filters;

  const [selectedValues, setSelectedValues] = useState({});

  // Filters already shown on this page: a filter the user removed with X
  // must not be added back when the page recomputes `filters`
  const initializedFiltersRef = useRef({ pathname: null, names: new Set() });

  // One debounce per text filter (created in debouncedSearch below)
  const debouncersRef = useRef({});
  const cancelPendingInput = (name) => {
    debouncersRef.current[name]?.cancel();
  };

  const handleSelectMenuItem = ({ key }) => {
    const currentParams = Object.fromEntries([...searchParams]);
    setMenuSelectedKeys((s) => [key, ...s]);
    const filter = filters.find((i) => i.name === key);
    setSearchParams({
      ...currentParams,
      [key]: '',
    });
    setShowFilters((s) => [...s, filter]);
  };

  useEffect(() => {
    if (showAllFilters) {
      console.log('----------- Set initial filters -----------');
      const initialFilters = {};
      const currentParams = Object.fromEntries([...searchParams]);
      if (initializedFiltersRef.current.pathname !== pathname) {
        initializedFiltersRef.current = { pathname, names: new Set() };
      }
      const initializedNames = initializedFiltersRef.current.names;
      (filters || []).forEach((filter) => {
        const value = currentParams[filter.name];
        if (value !== 'undefined') {
          if (value !== undefined) {
            initialFilters[filter.name] = value;
          } else if (!initializedNames.has(filter.name)) {
            initialFilters[filter.name] = '';
          }
        }
        initializedNames.add(filter.name);
        // range values live in `keys`, not in `name`
        if (filter.type === 'range') {
          (filter.keys || []).forEach((key) => {
            if (currentParams[key]) {
              initialFilters[key] = currentParams[key];
            }
          });
        }

        if (currentParams.date_to) {
          initialFilters['date_to'] = currentParams.date_to;
        }
        const adminVillage = pathname === '/admin/footfalls';

        if (initialFilters.group_by === '' && adminVillage) {
          initialFilters['group_by'] = 'day';
          setSelectedValues((prevState) => ({ ...prevState, group_by: 'day' }));
        }
      });
      // Update URL only if something changed, without a new history entry
      const nextSearch = new URLSearchParams(initialFilters);
      nextSearch.sort();
      const currentSearch = new URLSearchParams(searchParams);
      currentSearch.sort();
      if (nextSearch.toString() !== currentSearch.toString()) {
        setSearchParams(initialFilters, { replace: true });
      }
      setShowFilters(filters);
    }
  }, [filters, showAllFilters]);

  useEffect(() => {
    if (sortFilters && showFilters.length >= 2) {
      const firstFilter = showFilters[0];
      const secondFilter = showFilters[1];
      const selectedItem = firstFilter?.values?.find(
        (item) => item.value.toString() === firstFilter.defaultValue,
      );
      const insegneValues = secondFilter?.values;

      if (selectedItem && insegneValues) {
        setInsegnaVillage(selectedItem.title);
        setInsegnaValues(
          insegneValues.filter((item) =>
            item.title.includes(selectedItem.title),
          ),
        );
      }
    }
  }, [showFilters, sortFilters]);

  const handleDeselectMenuItem = ({ key }) => {
    // typing not applied yet must not bring the removed filter back
    cancelPendingInput(key);
    setMenuSelectedKeys((s) => s.filter((i) => i !== key));
    setShowFilters((s) => s.filter((i) => i.name !== key));
    const filter = filters.find((i) => i.name === key);
    onClose(filter);
  };

  const handleDeleteFilter = ({ name }) => {
    return () => {
      handleDeselectMenuItem({ key: name });
      setSelectedValues((prevState) => {
        const { [name]: _, ...rest } = prevState;
        return rest;
      });
    };
  };

  useEffect(() => {
    const selectedFilters = [];
    const selectedKeys = [];
    filters.forEach((i) => {
      switch (i.type) {
        case 'date': {
          const currentParams = Object.fromEntries([...searchParams]);
          if (currentParams.hasOwnProperty(i.name)) {
            const value = searchParams.get(i.name);
            let defaultValue = moment(value, 'DD-MM-YYYY');
            if (value.length === 7) {
              defaultValue = moment(value, 'MM-YYYY');
            }
            selectedKeys.push(i.name);
            selectedFilters.push({
              ...i,
              defaultValue: defaultValue.isValid() ? defaultValue : '',
            });
          }
          break;
        }
        case 'range': {
          const { keys } = i;
          const currentParams = Object.fromEntries([...searchParams]);
          if (keys && searchParams.get(keys[0]) && searchParams.get(keys[1])) {
            selectedKeys.push(i.name);
            selectedFilters.push({
              ...i,
              defaultValue: [
                moment(searchParams.get(keys[1]), i.format || 'DD-MM-YYYY'),
                moment(searchParams.get(keys[0]), i.format || 'DD-MM-YYYY'),
              ],
            });
          } else if (currentParams.hasOwnProperty(i.name)) {
            selectedKeys.push(i.name);
            selectedFilters.push({
              ...i,
            });
          }

          break;
        }
        case 'checkbox':
        case 'select':
        case 'input': {
          if (i.isActiveSearch) break;
          let value = searchParams.get(i.name);
          const currentParams = Object.fromEntries([...searchParams]);
          if (currentParams.hasOwnProperty(i.name)) {
            if (i.valueType === 'number' && value !== '') {
              value = parseInt(value);
            }
            if (value === 0) {
              selectedKeys.push(i.name);
              selectedFilters.push({
                ...i,
                defaultValue: value,
              });
            } else {
              selectedKeys.push(i.name);
              selectedFilters.push({
                ...i,
                defaultValue: value || '',
              });
            }
          }
          break;
        }
        default:
          break;
      }
    });
    setMenuSelectedKeys(selectedKeys);
    setShowFilters(selectedFilters);
  }, [filters, searchParams]);

  const handleRemoveFilters = useCallback(() => {
    Object.values(debouncersRef.current).forEach((d) => d.cancel());
    setMenuSelectedKeys([]);
    setShowFilters([]);
    setSelectedValues({});
    setSearchParams({});
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menu = (
    <Menu
      className={styles.menu}
      selectedKeys={menuSelectedKeys}
      multiple
      selectable
      onSelect={handleSelectMenuItem}
      onDeselect={handleDeselectMenuItem}
    >
      {filters.map((i) => {
        if (i.hide || i.isActiveSearch || i.allHide) return null;

        if (Array.isArray(avilableFilters) && !allFilters) {
          if (i.type === 'range') {
            if (_.difference(i.keys, avilableFilters).length !== 0) {
              return null;
            }
          } else {
            if (!avilableFilters.includes(i.name)) return null;
          }
        }
        const isSelected = menuSelectedKeys.includes(i.name);
        return (
          <Menu.Item key={i.name}>
            <Row justify={'space-between'}>
              <Col>
                <Typography.Text strong={isSelected}>{i.title}</Typography.Text>
              </Col>
              <Col>
                {!isSelected && <PlusOutlined />}
                {isSelected && <CheckOutlined className={styles.checkIcon} />}
              </Col>
            </Row>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  // One debounce per text filter; it calls the latest onChange so it uses
  // the current URL params (not the ones from when typing started)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const debouncedSearch = useCallback((filter, value) => {
    if (!debouncersRef.current[filter.name]) {
      debouncersRef.current[filter.name] = debounce(
        (f, v) => onChangeRef.current(f, { target: { value: v } }),
        INPUT_DEBOUNCE_MS,
      );
    }
    debouncersRef.current[filter.name](filter, value);
  }, []);

  // Leaving the page: drop pending typing so it isn't applied to another page
  useEffect(
    () => () => {
      Object.values(debouncersRef.current).forEach((d) => d.cancel());
    },
    [],
  );

  const onChangeFilters = (filter, isInput = false) => {
    return (...values) => {
      if (sortFilters && filter.title === 'Outlet Village') {
        const villageTitle = values[1]?.children;
        const insegnaFilter = showFilters.find((f) => f.title === 'Insegna');
        if (insegnaFilter?.values) {
          const newInsegna = insegnaFilter.values.filter((item) =>
            item?.title.includes(villageTitle),
          );
          setInsegnaValues(newInsegna);
          setInsegnaVillage(villageTitle);
        }
      }

      isInput
        ? debouncedSearch(filter, ...values)
        : onChange(filter, ...values);
    };
  };

  // Inactive companies of an Insegna filter (`inactiveValues`), narrowed to
  // the selected village like the active ones when sortFilters is used
  const getInactiveOptions = (filter) => {
    const inactive = filter?.inactiveValues || [];
    if (filter?.title === 'Insegna' && insegnaValues.length > 0) {
      return inactive.filter((item) => item?.title?.includes(insegnaVillage));
    }
    return inactive;
  };

  useEffect(() => {
    const autoSelectSingleOption = (filterTitle, customValues) => {
      const foundFilter = showFilters.find((f) => f.title === filterTitle);
      if (!foundFilter) return;

      const actualValues = customValues?.length
        ? customValues
        : foundFilter.values || [];

      // with inactive options there is a real choice: don't auto-select
      if (
        actualValues.length === 1 &&
        !getInactiveOptions(foundFilter).length
      ) {
        const [singleOption] = actualValues;
        const { name } = foundFilter;
        if (!searchParams.get(name)) {
          onChangeFilters(foundFilter)(singleOption.value);
          setSelectedValues((prev) => ({
            ...prev,
            [name]: singleOption.value,
          }));
        }
      }
    };

    autoSelectSingleOption('Insegna', insegnaValues);

    autoSelectSingleOption('Outlet Village', villageValues);
  }, [
    showFilters,
    insegnaValues,
    villageValues,
    searchParams,
    onChangeFilters,
  ]);

  const activeSeach = filters?.find((i) => i.isActiveSearch);
  const filtersCount = filters?.filter(
    (i) => !i.hide && !i.isActiveSearch,
  )?.length;

  // Value chosen in this session, otherwise the one from the URL (e.g. when
  // coming back to the list or reloading). URL values are strings, so match
  // the option by string to keep its real type (number ids).
  const getSelectValue = (filter, options) => {
    if (Object.prototype.hasOwnProperty.call(selectedValues, filter.name)) {
      return selectedValues[filter.name] || '';
    }
    const urlValue = filter.defaultValue;
    if (urlValue === undefined || urlValue === null || urlValue === '') {
      return '';
    }
    const option = options?.find((o) => String(o.value) === String(urlValue));
    return option ? option.value : '';
  };

  const handleSelect = (value, filterName) => {
    if (filterName === 'shopping_center_id') {
      setSelectedValues((prevState) => ({
        ...prevState,
        [filterName]: value,
        company: null,
      }));
    } else if (filterName === 'by_shopping_center') {
      setSelectedValues((prevState) => ({
        ...prevState,
        [filterName]: value,
        by_company: null,
      }));
    } else {
      setSelectedValues((prevState) => ({
        ...prevState,
        [filterName]: value,
      }));
    }
  };

  return (
    <Row wrap={false}>
      <Col flex={1}>
        <Row gutter={[16, 16]}>
          {showFilters?.map((f) => {
            if (f.hide || f.isActiveSearch || f.allHide) return null;
            if (Array.isArray(avilableFilters) && !allFilters) {
              if (f.type === 'range') {
                if (_.difference(f.keys, avilableFilters).length !== 0) {
                  return null;
                }
              } else {
                if (!avilableFilters.includes(f.name)) return null;
              }
            }

            if (f.type === 'select') {
              const searchProps = Helpers.addSearchProps();

              let optionsToRender = f.values;
              if (f.title === 'Insegna' && insegnaValues.length > 0) {
                optionsToRender = insegnaValues;
              } else if (
                f.title === 'Outlet Village' &&
                villageValues.length > 0
              ) {
                optionsToRender = villageValues;
              }
              const inactiveToRender = getInactiveOptions(f);

              return (
                <Col key={f.name}>
                  <FilterWrapper
                    title={f.title}
                    onClose={handleDeleteFilter(f)}
                  >
                    <Select
                      bordered={false}
                      placeholder={f.title}
                      style={{ minWidth: 120 }}
                      dropdownMatchSelectWidth={false}
                      {...searchProps}
                      onSelect={(value) => {
                        handleSelect(value, f.name);
                        onChangeFilters(f)(value);
                      }}
                      value={getSelectValue(f, [
                        ...(optionsToRender || []),
                        ...inactiveToRender,
                      ])}
                    >
                      {inactiveToRender.length
                        ? renderGroupedShopOptions({
                            shops: optionsToRender,
                            inactiveShops: inactiveToRender,
                            t,
                          })
                        : optionsToRender?.map((m, index) => (
                            <Option key={index} value={m.value}>
                              {m.title}
                            </Option>
                          ))}
                    </Select>
                  </FilterWrapper>
                </Col>
              );
            }

            if (f.type === 'range') {
              return (
                <Col
                  key={`${f.name}:${(f.keys || [])
                    .map((k) => searchParams.get(k))
                    .join('|')}`}
                >
                  <FilterWrapper
                    title={f.title}
                    onClose={handleDeleteFilter(f)}
                  >
                    <RangePicker
                      format={f.format || 'DD-MMMM-YYYY'}
                      bordered={false}
                      locale={locale}
                      onChange={onChangeFilters(f)}
                      defaultValue={f.defaultValue || ''}
                      picker={f.picker}
                    />
                  </FilterWrapper>
                </Col>
              );
            }

            if (f.type === 'date') {
              return (
                <Col key={`${f.name}:${searchParams.get(f.name)}`}>
                  <FilterWrapper
                    title={f.title}
                    onClose={handleDeleteFilter(f)}
                  >
                    <DatePicker
                      format={f.showFormat || 'DD MMMM YYYY'}
                      bordered={false}
                      locale={locale}
                      placeholder={f.title}
                      onChange={onChangeFilters(f)}
                      defaultValue={f.defaultValue || ''}
                      picker={f.picker}
                    />
                  </FilterWrapper>
                </Col>
              );
            }

            if (f.type === 'input') {
              return (
                <Col key={f.name}>
                  <FilterWrapper
                    title={f.title}
                    onClose={handleDeleteFilter(f)}
                  >
                    <FilterInput
                      placeholder={f.title}
                      bordered={false}
                      onChange={onChangeFilters(f, true)}
                      urlValue={searchParams.get(f.name) || ''}
                    />
                  </FilterWrapper>
                </Col>
              );
            }

            if (f.type === 'checkbox') {
              const checkedValue = f.paramValue || '1';
              return (
                <Col key={`${f.name}:${searchParams.get(f.name)}`}>
                  <FilterWrapper
                    title={f.title}
                    onClose={handleDeleteFilter(f)}
                  >
                    <Checkbox
                      className={styles.checkbox}
                      onChange={onChangeFilters(f)}
                      defaultChecked={f.defaultValue === checkedValue}
                    />
                  </FilterWrapper>
                </Col>
              );
            }
            return null;
          })}
          {filtersCount > 0 && (
            <Col>
              <Dropdown overlay={menu} trigger={['click']}>
                <Button
                  iconMargin={showFilters.length < 3}
                  type={'grey'}
                  icon={<FilterIcon />}
                >
                  {showFilters.length < 3 && 'Filtra'}
                </Button>
              </Dropdown>
            </Col>
          )}
          {!!showFilters.length && (
            <Col>
              <Button
                iconMargin={true}
                type={'red'}
                icon={<DeleteIcon />}
                onClick={handleRemoveFilters}
              >
                {t('Cancella i filtri')}
              </Button>
            </Col>
          )}
        </Row>
      </Col>

      {activeSeach && (
        <Col key={activeSeach.name}>
          <FilterWrapper
            title={activeSeach.title}
            onClose={handleDeleteFilter(activeSeach)}
            hideClose={true}
          >
            <FilterInput
              placeholder={activeSeach.title}
              bordered={false}
              onChange={onChangeFilters(activeSeach, true)}
              urlValue={searchParams.get(activeSeach.name) || ''}
            />
          </FilterWrapper>
        </Col>
      )}
    </Row>
  );
};

Filters.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      type: PropTypes.oneOf(['select', 'range', 'input', 'date']).isRequired,
      name: PropTypes.string,
      isSearch: PropTypes.bool,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any,
          title: PropTypes.string,
        }),
      ),
    }),
  ),
  filterByShoppingCenter: PropTypes.bool,
  allFilters: PropTypes.bool,
  defaultShowAllFilters: PropTypes.bool,
  sortFilters: PropTypes.bool,
};

export default React.memo(Filters);
